import React, { useEffect, useState } from 'react';
import { ReactThreeFiber } from 'react-three-fiber';
import { Mesh } from 'three';
import Timeline from './timeline';
import Button from '../button';
import { useTexture, useMedia } from '../../../hooks';

import playBtnIcon from '../../../assets/images/xr/icons/play-icon.png';
import pauseBtnIcon from '../../../assets/images/xr/icons/pause-icon.png';
import muteBtnIcon from '../../../assets/images/xr/icons/icn_audioOff.png';
import unmuteBtnIcon from '../../../assets/images/xr/icons/icn_audioOn.png';
import storyBtnIcon from '../../../assets/images/xr/icons/icn_story.png';
import isFunction from 'lodash/isFunction';
import Quality from './quality';
import { MediaEvents } from '../../../common/media';

type PlayerProps = ReactThreeFiber.Object3DNode<Mesh, typeof Mesh> & {
  target: string;
  mode?: 'story' | 'video' | 'slideshow' | 'simple';
  onOpenStory?: () => void;
};

const Player = ({ target, mode = 'simple', ...props }: PlayerProps) => {
  const { texture: btnPlayTxt } = useTexture(playBtnIcon);
  const { texture: btnPauseTxt } = useTexture(pauseBtnIcon);
  const { texture: btnMuteTxt } = useTexture(muteBtnIcon);
  const { texture: btnUnmuteTxt } = useTexture(unmuteBtnIcon);
  const { texture: btnStoryTxt } = useTexture(storyBtnIcon);
  const media = useMedia(target);
  const [playing, setPlaying] = useState(!media?.isPaused());
  const [muted, setMuted] = useState(!media?.isMuted());

  useEffect(() => {
    if (media) {
      media.on(MediaEvents.Play, () => setPlaying(true));
      media.on(MediaEvents.Pause, () => setPlaying(false));
      media.on(MediaEvents.Change_Mute, m => setMuted(m));

      setMuted(media.isMuted());
    }
  }, [media]);

  return (
    <mesh {...props}>
      <planeBufferGeometry args={[0.52, 0.05]} attach="geometry" />
      <meshBasicMaterial attach="material" color="#2c2b2c" />

      <group position={[-0.01, 0, 0.001]}>
        <Button
          position={[-0.22, 0, 0]}
          defaultState={{
            main: {
              size: 0.018,
              geometry: 'circle',
            },
            icon: {
              size: 0.01,
              material: {
                transparent: true,
                map: playing ? btnPauseTxt : btnPlayTxt,
              },
            },
          }}
          onClick={() => {
            if (playing) {
              media?.pause();
            } else {
              media?.play();
            }
          }}
        />
        <Timeline target={target} />
      </group>

      <group position={[0.19, 0, 0.001]}>
        <Button
          defaultState={{
            main: {
              size: 0.03,
              material: {
                transparent: true,
                map: muted ? btnMuteTxt : btnUnmuteTxt,
              },
            },
          }}
          onClick={() => {
            if (muted) {
              media?.unmute();
            } else {
              media?.mute();
            }
          }}
        />

        {mode === 'story' ? (
          <Button
            onClick={() => (isFunction(props.onOpenStory) ? props.onOpenStory() : null)}
            position={[0.04, 0, 0]}
            defaultState={{
              main: {
                size: 0.03,
                material: {
                  transparent: true,
                  map: btnStoryTxt,
                },
              },
            }}
          />
        ) : null}

        {mode === 'video' ? <Quality target={target} position={[0.04, 0, 0]} /> : null}
      </group>
    </mesh>
  );
};

export default Player;
